import React, { useContext } from "react";
import { Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_ABOUT_PRODUCT,
  PAGE_ROUTE_ADD_PRODUCT_ITEM_SOP,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import OrderSopGuideSections from "./OrderSopGuideSections";
import { ORDER_RUN_BOOK_GUIDE_BY_ID } from "./constants/orderRunBooksGuideContent";
import {
  publicInfoBodySx,
  publicInfoLinkSx
} from "./constants/publicInfoPageStyles";

type OrderSopSubguidePageProps = {
  guideId: string;
};

const OrderSopSubguidePage: React.FC<OrderSopSubguidePageProps> = ({
  guideId
}) => {
  const guide = ORDER_RUN_BOOK_GUIDE_BY_ID[guideId];
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  if (guide == null) {
    return null;
  }

  const pathFor = (route: string) =>
    language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;

  const sopsHubPath = pathFor(PAGE_ROUTE_SOPS);
  const addProductItemSopPath = pathFor(PAGE_ROUTE_ADD_PRODUCT_ITEM_SOP);
  const aboutProductPath = pathFor(PAGE_ROUTE_ABOUT_PRODUCT);
  const userGuidePath = pathFor(PAGE_ROUTE_USER_GUIDE);
  const contactPath = pathFor(PAGE_ROUTE_CONTACT);

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId={guide.pageTestId}
        eyebrow={t("order-sop-subguide-page-eyebrow", "Run books · Orders")}
        title={t(guide.titleKey, guide.titleFallback)}
        intro={t(guide.introKey, guide.introFallback)}
      />

      <PublicInfoCallout testId={`${guide.pageTestId}-note`}>
        {t(guide.noteKey, guide.noteFallback)}{" "}
        <MuiLink
          component={Link}
          to={sopsHubPath}
          underline="hover"
          sx={publicInfoLinkSx}
        >
          {t("order-sop-subguide-back-to-run-books-link", "Run books")}
        </MuiLink>
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        <OrderSopGuideSections
          sections={guide.sections}
          sectionTestIdPrefix={`${guide.id}-sop-section`}
          imageTestIdPrefix={`${guide.id}-sop-image`}
        />
      </Stack>

      <PublicInfoCtaPanel
        title={t(
          "order-sop-subguide-page-cta-title",
          "Need stock before you sell?"
        )}
        testId={`${guide.pageTestId}-cta`}
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "order-sop-subguide-page-cta-body",
            "Products must be in stock before you can sell them. Add products and receive stock first, then return to the order guides."
          )}{" "}
          <MuiLink
            component={Link}
            to={sopsHubPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("order-sop-subguide-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("order-sop-subguide-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={addProductItemSopPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "order-sop-subguide-page-cta-add-product-item-link",
              "How to add a product item"
            )}
          </MuiLink>
          {t("order-sop-subguide-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={aboutProductPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "order-sop-subguide-page-cta-about-product-link",
              "Products vs. product items"
            )}
          </MuiLink>
          {t("order-sop-subguide-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("order-sop-subguide-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("order-sop-subguide-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("order-sop-subguide-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default OrderSopSubguidePage;
